import { maskDecimal } from '@givebutter/util';
import { logError } from 'helpers/errors';
import { isEmoji } from 'helpers/regex';
import { NumberFormat, parsePhoneNumber } from 'libphonenumber-js';
import { last, split } from 'lodash';

const getFormattedPhoneNumberOrFail = (
    value: string | undefined,
    format: NumberFormat = 'INTERNATIONAL',
) => {
    if (typeof value === 'string') {
        const phoneNumber = parsePhoneNumber(value);

        if (phoneNumber.isValid()) {
            const formatted = phoneNumber.format(format);

            if (phoneNumber.countryCallingCode === '1') {
                // Can't use (?=\s.*) – lookbehind not supported in Safari
                return formatted.replace(/\s/g, '-').replace(/-/, ' ');
            }

            return formatted;
        }
    }

    throw new Error('Invalid phone number supplied.');
};

const getFormattedPhoneNumber = (value: string, format: NumberFormat = 'INTERNATIONAL') => {
    try {
        return getFormattedPhoneNumberOrFail(value, format);
    } catch (error) {
        return value;
    }
};

const transformCurrencyToCompactNumberFormat = (value: number, minimumValue = 999) => {
    if (value > minimumValue) {
        try {
            return Intl.NumberFormat('en', {
                notation: 'compact',
                maximumFractionDigits: 2,
            }).format(value as number);
        } catch (e) {
            logError(e as Error);
        }
    }
    return maskDecimal(value);
};

const truncateText = (text: string, maxCharacters = 50): string =>
    text.length > maxCharacters ? text.substring(0, maxCharacters).trimEnd() + '...' : text;

const formatUploadedFileName = (url: string): string => {
    if (!url) {
        return '';
    }

    const fileName = last(split(url, '/'));
    if (!fileName) {
        return '';
    }

    const fileNameWithoutQuery = fileName.split('?')[0];
    const extensionMatch = fileNameWithoutQuery.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '';

    let nameWithoutExtension = extension
        ? fileNameWithoutQuery.slice(0, -extension.length)
        : fileNameWithoutQuery;

    const yearMatch = nameWithoutExtension.match(/-\d{4}-/);

    if (yearMatch) {
        const [primaryName] = nameWithoutExtension.split(yearMatch[0]);
        nameWithoutExtension = primaryName;
    }

    while (nameWithoutExtension.endsWith('-')) {
        nameWithoutExtension = nameWithoutExtension.slice(0, -1);
    }
    nameWithoutExtension = nameWithoutExtension.trim();

    return nameWithoutExtension + extension;
};

const getSlugFromGeneric = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-') // Replace one or more spaces with a single hyphen
        .replace(/\.{2,}$/g, '') // Remove multiple consecutive periods at the end
        .replace(/[^a-z0-9\s.-]/g, '') // Remove characters that are not URL-safe
        .replace(/\./g, '-') // Replace remaining periods with hyphens
        .replace(/-+/g, '-'); // Replace multiple consecutive hyphens with a single hyphen

const getInitialsFromName = (firstName: string, lastName: string) => {
    const firstInitial = firstName.replace(isEmoji, '').trim().at(0);
    const secondInitial = lastName.replace(isEmoji, '').trim().at(0);
    return `${firstInitial ?? ''}${secondInitial ?? ''}`;
};

const getGenericPathFromPath = (value: string) => {
    // Examples
    // accounts/100/home -> home
    // accounts/100/payouts/history -> payouts/history
    // accounts/100/campaigns/33 -> campaigns/CAMPAIGNS_ID
    // accounts/100/campaigns/33/auction/items -> campaigns/CAMPAIGNS_ID/auction/items
    // accounts/100/contacts/2/profile -> contacts/CONTACTS_ID/profile

    // grabs everything after accounts/NUMBER/
    const accountSubPathPattern = /^\/accounts\/\d+\/(.*)$/;

    // used to try to replace any instance of (start of string or /)(LETTERS)/(NUMBERS)(end of string or /) with (LETTERS/UPPERCASE_LETTERS_ID)
    const entityIdPattern = /(?:^|\/)([a-zA-Z]+)\/(\d+)(?=\/|$)/g;

    const genericPath = accountSubPathPattern
        .exec(value)?.[1]
        .replace(entityIdPattern, (fullMatch, letters) => {
            const prefix = fullMatch.startsWith('/') ? '/' : '';
            return `${prefix}${letters}/${letters.toUpperCase()}_ID`;
        });

    return genericPath;
};

export {
    transformCurrencyToCompactNumberFormat,
    truncateText,
    getSlugFromGeneric,
    formatUploadedFileName,
    getInitialsFromName,
    getFormattedPhoneNumber,
    getFormattedPhoneNumberOrFail,
    getGenericPathFromPath,
};
